import React from 'react';
import { cn } from '../../lib/utils';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

/**
 * Enhanced Form Components for URGAA
 * Modern, clean form elements with consistent styling
 */

// Form Container
export const FormContainer = ({ children, className }) => (
  <div className={cn(
    'bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700',
    'shadow-sm p-5 transition-all duration-200 hover:shadow-md',
    className
  )}>
    {children}
  </div>
);

// Form Section with Icon
export const FormSection = ({ title, icon: Icon, children, className }) => (
  <div className={cn('mb-5 last:mb-0', className)}>
    {title && (
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
        {title}
      </h3>
    )}
    {children}
  </div>
);

// Form Grid
export const FormGrid = ({ columns = 2, children, className }) => (
  <div className={cn(
    'grid gap-4',
    columns === 1 && 'grid-cols-1',
    columns === 2 && 'grid-cols-1 md:grid-cols-2',
    columns === 3 && 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    columns === 4 && 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    className
  )}>
    {children}
  </div>
);

// Form Field Wrapper
export const FormField = ({ 
  label, 
  required = false, 
  error, 
  success,
  hint,
  className,
  fullWidth = false,
  children 
}) => (
  <div className={cn(
    'space-y-1.5',
    fullWidth && 'md:col-span-2 lg:col-span-full',
    className
  )}>
    {label && (
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
    )}
    {children}
    {error && (
      <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
        <AlertCircle size={12} />
        {error}
      </p>
    )}
    {success && !error && (
      <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
        <CheckCircle2 size={12} />
        {success}
      </p>
    )}
    {hint && !error && !success && (
      <p className="text-xs text-gray-500 mt-1">{hint}</p>
    )}
  </div>
);

// Enhanced Input
export const EnhancedInput = React.forwardRef(({ 
  error, 
  className,
  ...props 
}, ref) => (
  <input
    ref={ref}
    className={cn(
      'w-full px-3 py-2.5 text-sm rounded-lg transition-all duration-200',
      'bg-white dark:bg-gray-900',
      'border focus:ring-2 focus:ring-offset-0 outline-none',
      error 
        ? 'border-red-300 focus:border-red-500 focus:ring-red-200 bg-red-50 dark:bg-red-900/20' 
        : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-200 dark:focus:ring-blue-800',
      'placeholder:text-gray-400 dark:placeholder:text-gray-500',
      'text-gray-900 dark:text-white',
      className
    )}
    {...props}
  />
));
EnhancedInput.displayName = 'EnhancedInput';

// Enhanced Select
export const EnhancedSelect = React.forwardRef(({ 
  error, 
  options = [], 
  placeholder,
  className,
  ...props 
}, ref) => (
  <select
    ref={ref}
    className={cn(
      'w-full px-3 py-2.5 text-sm rounded-lg transition-all duration-200',
      'bg-white dark:bg-gray-900',
      'border focus:ring-2 focus:ring-offset-0 outline-none',
      error 
        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
        : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-200 dark:focus:ring-blue-800',
      'text-gray-900 dark:text-white',
      className
    )}
    {...props}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(opt => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </select>
));
EnhancedSelect.displayName = 'EnhancedSelect';

// Enhanced Textarea
export const EnhancedTextarea = React.forwardRef(({ 
  error, 
  rows = 3,
  className,
  ...props 
}, ref) => (
  <textarea
    ref={ref}
    rows={rows}
    className={cn(
      'w-full px-3 py-2.5 text-sm rounded-lg transition-all duration-200 resize-none',
      'bg-white dark:bg-gray-900',
      'border focus:ring-2 focus:ring-offset-0 outline-none',
      error 
        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
        : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-200 dark:focus:ring-blue-800',
      'placeholder:text-gray-400 dark:placeholder:text-gray-500',
      'text-gray-900 dark:text-white',
      className
    )}
    {...props}
  />
));
EnhancedTextarea.displayName = 'EnhancedTextarea';

// Primary Button
export const PrimaryButton = ({ 
  children, 
  loading = false, 
  disabled = false,
  icon: Icon,
  className,
  ...props 
}) => (
  <button
    disabled={loading || disabled}
    className={cn(
      'inline-flex items-center justify-center gap-2 px-5 py-2.5',
      'text-sm font-medium text-white rounded-lg',
      'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800',
      'shadow-sm hover:shadow-md transition-all duration-200',
      'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-sm',
      className
    )}
    {...props}
  >
    {loading ? (
      <>
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        <span>Processing...</span>
      </>
    ) : (
      <>
        {Icon && <Icon className="w-4 h-4" />}
        {children}
      </>
    )}
  </button>
);

// Secondary Button
export const SecondaryButton = ({ 
  children, 
  icon: Icon,
  className,
  ...props 
}) => (
  <button
    className={cn(
      'inline-flex items-center justify-center gap-2 px-5 py-2.5',
      'text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg',
      'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600',
      'hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200',
      'focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
      className
    )}
    {...props}
  >
    {Icon && <Icon className="w-4 h-4" />}
    {children}
  </button>
);

// Danger Button
export const DangerButton = ({ 
  children, 
  icon: Icon,
  className,
  ...props 
}) => (
  <button
    className={cn(
      'inline-flex items-center justify-center gap-2 px-4 py-2',
      'text-sm font-medium text-red-600 rounded-lg',
      'bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50',
      'transition-all duration-200',
      className
    )}
    {...props}
  >
    {Icon && <Icon className="w-4 h-4" />}
    {children}
  </button>
);

// Form Actions Container
export const FormActions = ({ children, className }) => (
  <div className={cn(
    'flex items-center justify-end gap-3 pt-4 mt-4',
    'border-t border-gray-200 dark:border-gray-700',
    className
  )}>
    {children}
  </div>
);

// Info Box
export const InfoBox = ({ children, variant = 'info', className }) => {
  const variants = {
    info: 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300',
    success: 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300',
    warning: 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-300',
    error: 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300',
  };

  return (
    <div className={cn(
      'p-3 rounded-lg border text-sm',
      variants[variant],
      className
    )}>
      {children}
    </div>
  );
};

export default {
  FormContainer,
  FormSection,
  FormGrid,
  FormField,
  EnhancedInput,
  EnhancedSelect,
  EnhancedTextarea,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  FormActions,
  InfoBox,
};
